<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import BaseCard from '@/components/ui/BaseCard.vue';
import BaseButton from '@/components/ui/BaseButton.vue';
import BaseBadge from '@/components/ui/BaseBadge.vue';
import IssueDetailHeader from '@/components/issues/IssueDetailHeader.vue';
import AiSummaryDetailCard from '@/components/issues/AiSummaryDetailCard.vue';
import AiSuggestedActions from '@/components/issues/AiSuggestedActions.vue';
import GeneratedReplyCard from '@/components/issues/GeneratedReplyCard.vue';
import OriginalSources from '@/components/issues/OriginalSources.vue';
import AttachmentGrid from '@/components/issues/AttachmentGrid.vue';
import RelatedIssues from '@/components/issues/RelatedIssues.vue';
import CommentThread from '@/components/issues/CommentThread.vue';
import SeverityChanger from '@/components/issues/SeverityChanger.vue';
import CategoryChanger from '@/components/issues/CategoryChanger.vue';
import StatusChanger from '@/components/issues/StatusChanger.vue';
import { useIssue } from '@/composables/useIssue';
import { useIssuesStore } from '@/stores/issues';
import { CATEGORY_LABEL, SOURCE_LABEL, STATUS_LABEL } from '@/components/issues/issue-enums';
import type { components } from '@/api/generated/schema';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

type AddCommentRequest = components['schemas']['AddCommentRequest'];

const route = useRoute();
const id = computed(() => String(route.params['id']));

const { issue, isLoading, error, patch, addComment, resolve } = useIssue(id);
const store = useIssuesStore();

// Subscribe to issueChanged for this specific id
watch(
  () => store.entitiesById.get(id.value),
  (updated) => {
    if (updated && issue.value && updated.id === issue.value.id) {
      // Store was updated by SignalR — already reflected via store reference.
      // The store.handleIssueChanged fetches GET /v1/issues/{id} and upserts.
    }
  },
);

// Patch handlers
const isPatchingStatus = ref(false);
const isPatchingSeverity = ref(false);
const isPatchingCategory = ref(false);
const isAddingComment = ref(false);
const patchError = ref<string | null>(null);

async function onStatusChange(status: number) {
  isPatchingStatus.value = true;
  patchError.value = null;
  try {
    await patch({ status, severity: null, category: null, assignedToId: null });
  } catch (e) {
    patchError.value = e instanceof Error ? e.message : 'Update failed.';
  } finally {
    isPatchingStatus.value = false;
  }
}

async function onSeverityChange(severity: number) {
  isPatchingSeverity.value = true;
  patchError.value = null;
  try {
    await patch({ status: null, severity, category: null, assignedToId: null });
  } catch (e) {
    patchError.value = e instanceof Error ? e.message : 'Update failed.';
  } finally {
    isPatchingSeverity.value = false;
  }
}

async function onCategoryChange(category: number) {
  isPatchingCategory.value = true;
  patchError.value = null;
  try {
    await patch({ status: null, severity: null, category, assignedToId: null });
  } catch (e) {
    patchError.value = e instanceof Error ? e.message : 'Update failed.';
  } finally {
    isPatchingCategory.value = false;
  }
}

async function onAddComment(req: AddCommentRequest) {
  isAddingComment.value = true;
  try {
    await addComment(req);
  } finally {
    isAddingComment.value = false;
  }
}

async function onResolve() {
  try {
    await resolve();
  } catch (e) {
    patchError.value = e instanceof Error ? e.message : 'Resolve failed.';
  }
}

function onSuggestedActionComment(label: string) {
  // Pre-fill comment box with the action label — not yet implemented
  console.info('[IssueDetailView] draft comment for action:', label);
}
</script>

<template>
  <div data-testid="issue-detail-view">
    <!-- Loading skeleton -->
    <div v-if="isLoading" class="flex flex-col gap-4">
      <div class="h-24 bg-canvas-soft rounded-lg animate-pulse" />
      <div class="h-40 bg-canvas-soft rounded-lg animate-pulse" />
      <div class="h-64 bg-canvas-soft rounded-lg animate-pulse" />
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="p-8 text-center text-body text-red-600">
      {{ error }}
    </div>

    <!-- Detail content -->
    <template v-else-if="issue">
      <!-- Header (breadcrumb + title + badges + prev/next) -->
      <IssueDetailHeader :issue="issue" class="mb-4" />

      <!-- Error banner for write failures -->
      <div v-if="patchError" class="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-caption text-red-700" role="alert">
        {{ patchError }}
      </div>

      <!-- Two-column layout: main + side panel -->
      <div class="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <!-- ── Main column ── -->
        <div class="flex flex-col gap-6 min-w-0">
          <!-- AI summary card (CLAUDE.md: must carry AICard + teal eyebrow) -->
          <AiSummaryDetailCard v-if="issue.aiSummaryText" :issue="issue" />

          <!-- AI suggested actions -->
          <AiSuggestedActions
            :actions="issue.suggestedActions"
            @comment="onSuggestedActionComment"
          />

          <!-- Generated resident reply (AI card + teal gradient) -->
          <GeneratedReplyCard
            v-if="issue.generatedResidentReply"
            :reply="issue.generatedResidentReply"
          />

          <!-- Original source -->
          <OriginalSources :issue="issue" />

          <!-- Attachments -->
          <AttachmentGrid :photo-urls="issue.photoUrls" />

          <!-- Related issues (teal footer AI insight) -->
          <RelatedIssues :related="issue.relatedIssues" />

          <!-- Board discussion / comments -->
          <BaseCard padding="md">
            <template #header>
              <h2 class="text-section text-fg font-semibold">
                Board discussion
                <span class="text-caption text-muted font-mono tabular-nums ml-1">({{ issue.comments.length }})</span>
              </h2>
            </template>
            <!-- Task 15 (D-P6): pass issueSource + hasPortalSubmitter so
                 CommentThread can show "Send to public submitter" checkbox. -->
            <CommentThread
              :comments="issue.comments"
              :is-submitting="isAddingComment"
              :issue-source="issue.source"
              :has-portal-submitter="issue.hasPortalSubmitter"
              @submit="onAddComment"
            />
          </BaseCard>

          <!-- Activity timeline -->
          <BaseCard padding="md" data-testid="activity-timeline">
            <template #header>
              <h2 class="text-section text-fg font-semibold">Activity timeline</h2>
            </template>
            <ol class="flex flex-col gap-3">
              <li
                v-for="entry in issue.auditLog"
                :key="entry.id"
                class="flex items-start gap-3 border-b border-border last:border-0 pb-3 last:pb-0"
              >
                <!-- AI vs human actor icon -->
                <div
                  :class="[
                    'flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-caption',
                    entry.actorIsAi ? 'bg-teal-100 text-teal-700' : 'bg-navy-100 text-navy-700',
                  ]"
                  :title="entry.actorIsAi ? 'AI' : entry.actorName"
                  aria-hidden="true"
                >
                  {{ entry.actorIsAi ? '★' : entry.actorName.charAt(0).toUpperCase() }}
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-caption text-fg">
                    <span class="font-medium">{{ entry.actorName }}</span>
                    {{ ' — ' }}{{ entry.body }}
                  </p>
                  <div v-if="entry.previousValue || entry.newValue" class="flex items-center gap-1.5 mt-0.5 text-caption text-muted">
                    <span v-if="entry.previousValue" class="line-through">{{ entry.previousValue }}</span>
                    <span v-if="entry.previousValue && entry.newValue" aria-hidden="true">→</span>
                    <span v-if="entry.newValue" class="text-fg">{{ entry.newValue }}</span>
                  </div>
                  <time :datetime="entry.at" class="text-caption text-muted font-mono tabular-nums mt-0.5 block">
                    {{ dayjs(entry.at).fromNow() }}
                  </time>
                </div>
              </li>
              <li v-if="issue.auditLog.length === 0" class="text-caption text-muted">
                No activity recorded.
              </li>
            </ol>
          </BaseCard>
        </div>

        <!-- ── Side panel ── -->
        <aside class="flex flex-col gap-4">
          <!-- Take action panel -->
          <BaseCard padding="md">
            <template #header>
              <h2 class="text-caption font-semibold text-fg uppercase tracking-wide">Take action</h2>
            </template>

            <div class="flex flex-col gap-3">
              <StatusChanger
                :current="issue.status"
                :disabled="isPatchingStatus"
                @change="onStatusChange"
              />

              <BaseButton
                variant="success"
                size="sm"
                :block="true"
                :loading="isPatchingStatus"
                @click="onResolve"
              >
                Mark resolved
              </BaseButton>

              <!-- Stub-disabled actions (alert, escalate, assign need future slices) -->
              <BaseButton variant="secondary" size="sm" :block="true" disabled title="Requires Alert Rules slice">
                Send text alert
              </BaseButton>
              <BaseButton variant="secondary" size="sm" :block="true" disabled title="Requires future slice">
                Generate reply
              </BaseButton>
            </div>
          </BaseCard>

          <!-- Properties panel -->
          <BaseCard padding="md">
            <template #header>
              <h2 class="text-caption font-semibold text-fg uppercase tracking-wide">Properties</h2>
            </template>

            <dl class="flex flex-col gap-3">
              <div>
                <dt class="text-caption text-muted">Severity</dt>
                <dd class="mt-0.5">
                  <SeverityChanger
                    :current="issue.severity"
                    :disabled="isPatchingSeverity"
                    @change="onSeverityChange"
                  />
                </dd>
              </div>
              <div>
                <dt class="text-caption text-muted">Category</dt>
                <dd class="mt-0.5">
                  <CategoryChanger
                    :current="issue.category"
                    :disabled="isPatchingCategory"
                    @change="onCategoryChange"
                  />
                </dd>
              </div>
              <div>
                <dt class="text-caption text-muted">Status</dt>
                <dd class="mt-0.5">
                  <BaseBadge :variant="issue.status === 3 ? 'resolved' : issue.status === 0 ? 'new' : 'progress'" :no-dot="true">
                    {{ STATUS_LABEL[issue.status] ?? String(issue.status) }}
                  </BaseBadge>
                </dd>
              </div>
              <div>
                <dt class="text-caption text-muted">Source</dt>
                <dd class="mt-0.5 text-body text-fg">{{ SOURCE_LABEL[issue.source] ?? String(issue.source) }}</dd>
              </div>
              <div>
                <dt class="text-caption text-muted">Category</dt>
                <dd class="mt-0.5 text-body text-fg">{{ CATEGORY_LABEL[issue.category] ?? String(issue.category) }}</dd>
              </div>
              <div v-if="issue.assignedToName">
                <dt class="text-caption text-muted">Assigned to</dt>
                <dd class="mt-0.5 text-body text-fg">{{ issue.assignedToName }}</dd>
              </div>
              <div v-if="issue.isRepeat">
                <dt class="text-caption text-muted">Repeat count</dt>
                <dd class="mt-0.5 text-body text-fg font-mono tabular-nums">{{ issue.repeatCount }}×</dd>
              </div>
            </dl>
          </BaseCard>

          <!-- Will notify panel -->
          <BaseCard v-if="issue.notifyTargets.length > 0" padding="md">
            <template #header>
              <h2 class="text-caption font-semibold text-fg uppercase tracking-wide">Will notify</h2>
            </template>
            <ul class="flex flex-col gap-2">
              <li
                v-for="target in issue.notifyTargets"
                :key="target.userId"
                class="flex items-center justify-between gap-2"
              >
                <span class="text-body text-fg">{{ target.name }}</span>
                <div class="flex items-center gap-1">
                  <span
                    v-for="m in target.methods"
                    :key="m"
                    class="text-caption text-muted bg-canvas-soft px-1.5 py-0.5 rounded"
                  >
                    {{ m }}
                  </span>
                </div>
              </li>
            </ul>
          </BaseCard>

          <!-- Tags panel -->
          <BaseCard v-if="issue.tags.length > 0" padding="md">
            <template #header>
              <h2 class="text-caption font-semibold text-fg uppercase tracking-wide">Tags</h2>
            </template>
            <div class="flex flex-wrap gap-1.5">
              <span
                v-for="tag in issue.tags"
                :key="tag"
                class="inline-flex items-center px-2 h-6 text-caption bg-canvas-soft text-muted border border-border rounded-pill"
              >
                {{ tag }}
              </span>
            </div>
          </BaseCard>
        </aside>
      </div>
    </template>

    <!-- Empty / not found -->
    <div v-else class="p-8 text-center text-body text-muted">
      Issue not found.
    </div>
  </div>
</template>
